import type { Distance, Margins, Point, Rectangle, SpatialRectangle } from "@pdfking/math"
import { ClampMargins } from "@pdfking/math"
import { SortBy } from "@pdfking/tfunc"

import { Direction, FlexEnum } from "../types/flex"
import type { Laid } from "../types/laid"

type Axis = { size: keyof Rectangle<unknown>; start: keyof Point<unknown> }

const AXIS: Record<Direction, Axis> = {
  [Direction.Column]: { size: "height", start: "y" },
  [Direction.Row]: { size: "width", start: "x" },
}

/** A laid out node with its share of the sheet around it. */
export type Alloted<D = unknown> = Omit<Laid<D>, "children"> & {
  bleed: Margins<Distance>
  children: Alloted<D>[]
}

// The tree is y-down: `top` is the smaller y, so these are not
// `MarginsBetween`'s edges.
const between = (
  box: SpatialRectangle<Distance>,
  rect: SpatialRectangle<Distance>
): Margins<Distance> =>
  ClampMargins({
    bottom: (box.y + box.height - rect.y - rect.height) as Distance,
    left: (rect.x - box.x) as Distance,
    right: (box.x + box.width - rect.x - rect.width) as Distance,
    top: (rect.y - box.y) as Distance,
  })

const end = (rect: SpatialRectangle<Distance>, axis: Axis): number =>
  rect[axis.start] + rect[axis.size]

/**
 * Hand every point of `box` to one of the pages inside it: a container splits
 * its own box along its direction — each child keeps its share up to the
 * midline with the next one, the outer two reach the box's own edges — and
 * passes each share down. A page's bleed is then whatever is left of its share
 * around it.
 *
 * The split is the layout's own, which is why a bleed never crosses into
 * another row: the row owns its band and only shares it with its own pages.
 * Nested boxes never overlap, so neither can two bleeds. The children come
 * back in the order they were laid.
 */
export const allot = <D>(node: Laid<D>, box: SpatialRectangle<Distance>): Alloted<D> => {
  const bleed = between(box, node.rect)
  if (node.item.enum === FlexEnum.Page) return { ...node, bleed, children: [] }
  const axis = AXIS[node.item.direction]
  const ordered = SortBy(node.children, child => child.rect[axis.start])
  const shares = new Map<Laid<D>, SpatialRectangle<Distance>>()
  ordered.forEach((child, i) => {
    const previous = ordered[i - 1]
    const next = ordered[i + 1]
    const start = previous
      ? (end(previous.rect, axis) + child.rect[axis.start]) / 2
      : box[axis.start]
    const stop = next ? (end(child.rect, axis) + next.rect[axis.start]) / 2 : end(box, axis)
    shares.set(child, { ...box, [axis.size]: stop - start, [axis.start]: start })
  })
  return { ...node, bleed, children: node.children.map(child => allot(child, shares.get(child)!)) }
}
